// ABOUTME: Builds recruiter-managed WhatsApp application flow drafts for job sharing.
// ABOUTME: Keeps frontend flow validation aligned with the backend publish contract.
import {
    normalizeWhatsappPreRegistrationAvailabilityValue
} from "./WhatsappApplicationPanel.helpers";
const WHATSAPP_APPLICATION_MAX_INTERACTIVE_OPTIONS = 10;

export type WhatsappApplicationNodeType =
    | "message"
    | "consent"
    | "resume"
    | "question"
    | "screeningQuestionnaire"
    | "end";

export type WhatsappApplicationConditionOperator =
    | "EQUALS"
    | "NOT_EQUALS"
    | "AGE_AT_LEAST"
    | "LESS_THAN"
    | "LESS_THAN_OR_EQUALS"
    | "GREATER_THAN"
    | "GREATER_THAN_OR_EQUALS"
    | "INCLUDES";

export interface WhatsappApplicationCondition {
    operator: WhatsappApplicationConditionOperator;
    value: string;
    nextNodeId: string;
    scoreDelta?: number;
    eliminatory?: boolean;
}

export interface WhatsappApplicationNode {
    editorKey: string;
    id: string;
    type: WhatsappApplicationNodeType;
    text: string;
    nextNodeId: string;
    answerKey: string;
    outcome: "QUALIFIED" | "NOT_QUALIFIED" | "";
    options: string[];
    conditions: WhatsappApplicationCondition[];
}

export interface WhatsappApplicationFlow {
    scope: "JOB" | "PRE_REGISTRATION";
    flowId?: string;
    modelId?: string;
    sourceModelId?: string;
    sourceModelVersion?: number;
    requireApplicationFormRequiredFields: boolean;
    collectScreeningQuestionnaire: boolean;
    applicationStartText: string;
    existingCandidateFoundText: string;
    existingCandidateInvalidChoiceText: string;
    consentInvalidAnswerText: string;
    resumeInvalidAnswerText: string;
    cvParsingStartedText: string;
    cvParsingCompletedText: string;
    name: string;
    qualifiedStageId: string;
    notQualifiedStageId: string;
    notQualifiedDisqualifyReason: Compleo.IObject | null;
    nodes: WhatsappApplicationNode[];
    publishedVersion: number;
}

export interface WhatsappApplicationDefaultFlowTexts {
    applicationStartText: string;
    existingCandidateFoundText: string;
    existingCandidateInvalidChoiceText: string;
    consentInvalidAnswerText: string;
    resumeInvalidAnswerText: string;
    consentText: string;
    questionText: string;
    qualifiedText: string;
    notQualifiedText: string;
    cvParsingStartedText: string;
    cvParsingCompletedText: string;
}

export interface WhatsappPreRegistrationDefaultFlowTexts {
    name: string;
    applicationStartText: string;
    consentText: string;
    candidateNameText: string;
    dateOfBirthText: string;
    underageText: string;
    positionText: string;
    availabilityText: string;
    morningOnlyText: string;
    resumeText: string;
    eligibleText: string;
    endText: string;
    existingCandidateFoundText: string;
    existingCandidateInvalidChoiceText: string;
    consentInvalidAnswerText: string;
    resumeInvalidAnswerText: string;
    cvParsingStartedText: string;
    cvParsingCompletedText: string;
}

export const fillWhatsappApplicationMessageTexts = (
    flow: WhatsappApplicationFlow,
    defaultFlowTexts: WhatsappApplicationDefaultFlowTexts
): WhatsappApplicationFlow =>
    flow.scope === "PRE_REGISTRATION"
        ? flow
        : {
              ...flow,
              applicationStartText:
                  flow.applicationStartText ||
                  defaultFlowTexts.applicationStartText,
              existingCandidateFoundText:
                  flow.existingCandidateFoundText ||
                  defaultFlowTexts.existingCandidateFoundText,
              existingCandidateInvalidChoiceText:
                  flow.existingCandidateInvalidChoiceText ||
                  defaultFlowTexts.existingCandidateInvalidChoiceText,
              consentInvalidAnswerText:
                  flow.consentInvalidAnswerText ||
                  defaultFlowTexts.consentInvalidAnswerText,
              resumeInvalidAnswerText:
                  flow.resumeInvalidAnswerText ||
                  defaultFlowTexts.resumeInvalidAnswerText,
              cvParsingStartedText:
                  flow.cvParsingStartedText ||
                  defaultFlowTexts.cvParsingStartedText,
              cvParsingCompletedText:
                  flow.cvParsingCompletedText ||
                  defaultFlowTexts.cvParsingCompletedText
          };

export const resolveWhatsappApplicationErrorMessage = (
    error: any,
    translate: (key: string) => string,
    fallback: string
) => {
    const translationCode = error?.response?.data?.translationCode;
    const hasTranslationCode =
        typeof translationCode === "string" && translationCode.trim();
    if (hasTranslationCode) {
        const translated = translate(translationCode);
        if (translated && translated !== translationCode) {
            return translated;
        }
    }

    const responseMessage = error?.response?.data?.message;
    if (hasTranslationCode && responseMessage === translationCode) {
        return error?.response?.data?.error?.message || fallback;
    }

    return (
        responseMessage ||
        error?.response?.data?.error?.message ||
        error?.message ||
        fallback
    );
};

export const getWhatsappApplicationMessageTimeout = (
    typeMessage: "success" | "error" | "warning",
    error?: any
) =>
    typeMessage === "error" &&
    error?.response?.data?.errorCode ===
        "WHATSAPP_APPLICATION_SCREENING_QUESTIONNAIRE_REQUIRED"
        ? null
        : 2500;

export const WHATSAPP_APPLICATION_NODE_TYPES: WhatsappApplicationNodeType[] = [
    "message",
    "consent",
    "resume",
    "question",
    "screeningQuestionnaire",
    "end"
];

export const WHATSAPP_APPLICATION_CONDITION_OPERATORS: WhatsappApplicationConditionOperator[] = [
    "EQUALS",
    "NOT_EQUALS",
    "AGE_AT_LEAST",
    "LESS_THAN",
    "LESS_THAN_OR_EQUALS",
    "GREATER_THAN",
    "GREATER_THAN_OR_EQUALS",
    "INCLUDES"
];

let whatsappApplicationNodeEditorKeySequence = 0;

export const createWhatsappApplicationNodeEditorKey = () =>
    `whatsapp-node-editor-${++whatsappApplicationNodeEditorKeySequence}`;

const normalizeNodeType = (type: unknown): WhatsappApplicationNodeType =>
    WHATSAPP_APPLICATION_NODE_TYPES.includes(
        type as WhatsappApplicationNodeType
    )
        ? (type as WhatsappApplicationNodeType)
        : "question";

const normalizeConditionOperator = (
    operator: unknown
): WhatsappApplicationConditionOperator =>
    WHATSAPP_APPLICATION_CONDITION_OPERATORS.includes(
        operator as WhatsappApplicationConditionOperator
    )
        ? (operator as WhatsappApplicationConditionOperator)
        : "EQUALS";

const getNodeText = (node: Compleo.IObject) =>
    String(
        node.text ||
            node.messageText ||
            node.questionText ||
            node.bodyText ||
            ""
    );

export const normalizeWhatsappApplicationFlow = (
    flow?: Compleo.IObject | null
): WhatsappApplicationFlow => ({
    scope: flow?.scope === "PRE_REGISTRATION" ? "PRE_REGISTRATION" : "JOB",
    flowId: flow?.flowId ? String(flow.flowId) : undefined,
    modelId: flow?.modelId ? String(flow.modelId) : undefined,
    sourceModelId: flow?.sourceModelId ? String(flow.sourceModelId) : undefined,
    sourceModelVersion: flow?.sourceModelVersion
        ? Number(flow.sourceModelVersion)
        : undefined,
    requireApplicationFormRequiredFields:
        flow?.requireApplicationFormRequiredFields === true,
    collectScreeningQuestionnaire:
        flow?.collectScreeningQuestionnaire !== false,
    applicationStartText: String(flow?.applicationStartText || ""),
    existingCandidateFoundText: String(flow?.existingCandidateFoundText || ""),
    existingCandidateInvalidChoiceText: String(
        flow?.existingCandidateInvalidChoiceText || ""
    ),
    consentInvalidAnswerText: String(flow?.consentInvalidAnswerText || ""),
    resumeInvalidAnswerText: String(flow?.resumeInvalidAnswerText || ""),
    cvParsingStartedText: String(flow?.cvParsingStartedText || ""),
    cvParsingCompletedText: String(flow?.cvParsingCompletedText || ""),
    name: String(flow?.name || ""),
    qualifiedStageId: String(flow?.qualifiedStageId || ""),
    notQualifiedStageId: String(flow?.notQualifiedStageId || ""),
    notQualifiedDisqualifyReason: flow?.notQualifiedDisqualifyReason || null,
    publishedVersion: Number(flow?.publishedVersion || 0),
    nodes: (Array.isArray(flow?.nodes) ? flow?.nodes : []).map(
        (node: Compleo.IObject, index: number) => ({
            editorKey:
                typeof node.editorKey === "string" && node.editorKey
                    ? node.editorKey
                    : createWhatsappApplicationNodeEditorKey(),
            id: String(node.id || `node_${index + 1}`),
            type: normalizeNodeType(node.type),
            text: getNodeText(node),
            nextNodeId: String(node.nextNodeId || ""),
            answerKey: String(node.answerKey || ""),
            outcome:
                node.outcome === "QUALIFIED" || node.outcome === "NOT_QUALIFIED"
                    ? node.outcome
                    : "",
            options: (Array.isArray(node.options)
                ? node.options
                : []
            ).map((option: unknown) => String(option ?? "")),
            conditions: (Array.isArray(node.conditions)
                ? node.conditions
                : []
            ).map((condition: Compleo.IObject) => ({
                operator: normalizeConditionOperator(condition.operator),
                value: String(condition.value ?? ""),
                nextNodeId: String(condition.nextNodeId || ""),
                scoreDelta:
                    condition.scoreDelta === undefined ||
                    condition.scoreDelta === ""
                        ? undefined
                        : Number(condition.scoreDelta),
                eliminatory: condition.eliminatory === true
            }))
        })
    )
});

export const buildDefaultWhatsappApplicationFlow = (
    jobTitle: string,
    texts: WhatsappApplicationDefaultFlowTexts
): WhatsappApplicationFlow => ({
    scope: "JOB",
    name: jobTitle || "WhatsApp",
    qualifiedStageId: "",
    notQualifiedStageId: "",
    notQualifiedDisqualifyReason: null,
    requireApplicationFormRequiredFields: true,
    collectScreeningQuestionnaire: true,
    applicationStartText: texts.applicationStartText,
    existingCandidateFoundText: texts.existingCandidateFoundText,
    existingCandidateInvalidChoiceText:
        texts.existingCandidateInvalidChoiceText,
    consentInvalidAnswerText: texts.consentInvalidAnswerText,
    resumeInvalidAnswerText: texts.resumeInvalidAnswerText,
    cvParsingStartedText: texts.cvParsingStartedText,
    cvParsingCompletedText: texts.cvParsingCompletedText,
    publishedVersion: 0,
    nodes: [
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "consent_1",
            type: "consent",
            text: texts.consentText,
            nextNodeId: "end_qualified",
            answerKey: "",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "end_qualified",
            type: "end",
            text: texts.qualifiedText,
            nextNodeId: "",
            answerKey: "",
            outcome: "QUALIFIED",
            options: [],
            conditions: []
        }
    ]
});

export const buildDefaultWhatsappPreRegistrationFlow = (
    texts: WhatsappPreRegistrationDefaultFlowTexts
): WhatsappApplicationFlow => ({
    scope: "PRE_REGISTRATION",
    name: texts.name,
    qualifiedStageId: "",
    notQualifiedStageId: "",
    notQualifiedDisqualifyReason: null,
    requireApplicationFormRequiredFields: false,
    collectScreeningQuestionnaire: false,
    applicationStartText: texts.applicationStartText,
    existingCandidateFoundText: texts.existingCandidateFoundText,
    existingCandidateInvalidChoiceText:
        texts.existingCandidateInvalidChoiceText,
    consentInvalidAnswerText: texts.consentInvalidAnswerText,
    resumeInvalidAnswerText: texts.resumeInvalidAnswerText,
    cvParsingStartedText: texts.cvParsingStartedText,
    cvParsingCompletedText: texts.cvParsingCompletedText,
    publishedVersion: 0,
    nodes: [
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "consent_1",
            type: "consent",
            text: texts.consentText,
            nextNodeId: "candidate_name",
            answerKey: "",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "candidate_name",
            type: "question",
            text: texts.candidateNameText,
            nextNodeId: "date_of_birth",
            answerKey: "candidateName",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "date_of_birth",
            type: "question",
            text: texts.dateOfBirthText,
            nextNodeId: "underage_message",
            answerKey: "dateOfBirth",
            outcome: "",
            options: [],
            conditions: [
                {
                    operator: "AGE_AT_LEAST",
                    value: "18",
                    nextNodeId: "position"
                }
            ]
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "underage_message",
            type: "message",
            text: texts.underageText,
            nextNodeId: "underage_end",
            answerKey: "",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "underage_end",
            type: "end",
            text: texts.endText,
            nextNodeId: "",
            answerKey: "",
            outcome: "NOT_QUALIFIED",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "position",
            type: "question",
            text: texts.positionText,
            nextNodeId: "availability",
            answerKey: "position",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "availability",
            type: "question",
            text: texts.availabilityText,
            nextNodeId: "morning_message",
            answerKey: "availability",
            outcome: "",
            options: ["MORNING_ONLY", "OTHER_SHIFTS"],
            conditions: [
                {
                    operator: "EQUALS",
                    value: "OTHER_SHIFTS",
                    nextNodeId: "resume"
                }
            ]
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "morning_message",
            type: "message",
            text: texts.morningOnlyText,
            nextNodeId: "morning_end",
            answerKey: "",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "morning_end",
            type: "end",
            text: texts.endText,
            nextNodeId: "",
            answerKey: "",
            outcome: "NOT_QUALIFIED",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "resume",
            type: "resume",
            text: texts.resumeText,
            nextNodeId: "eligible_message",
            answerKey: "resume",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "eligible_message",
            type: "message",
            text: texts.eligibleText,
            nextNodeId: "eligible_end",
            answerKey: "",
            outcome: "",
            options: [],
            conditions: []
        },
        {
            editorKey: createWhatsappApplicationNodeEditorKey(),
            id: "eligible_end",
            type: "end",
            text: texts.endText,
            nextNodeId: "",
            answerKey: "",
            outcome: "QUALIFIED",
            options: [],
            conditions: []
        }
    ]
});

export const getWhatsappApplicationModelScopeFromSearch = (search: string) =>
    new URLSearchParams(search).get("scope") === "PRE_REGISTRATION"
        ? "PRE_REGISTRATION"
        : "JOB";

export type SerializedWhatsappApplicationNode = Omit<
    WhatsappApplicationNode,
    "editorKey"
>;

export const serializeWhatsappApplicationFlowForSave = (
    flow: WhatsappApplicationFlow
): Omit<WhatsappApplicationFlow, "nodes"> & {
    nodes: SerializedWhatsappApplicationNode[];
} => ({
    ...flow,
    nodes: flow.nodes.map((node) => {
        const serializableNode = {
            ...(node as SerializedWhatsappApplicationNode & {
                editorKey?: string;
                clientKey?: string;
            })
        };
        delete serializableNode.editorKey;
        delete serializableNode.clientKey;

        return {
            ...serializableNode,
            ...(flow.scope === "PRE_REGISTRATION" &&
            serializableNode.answerKey === "availability"
                ? {
                      options: serializableNode.options.map(
                          normalizeWhatsappPreRegistrationAvailabilityValue
                      )
                  }
                : {}),
            conditions: serializableNode.conditions.map((condition) => ({
                ...condition,
                ...(flow.scope === "PRE_REGISTRATION" &&
                serializableNode.answerKey === "availability"
                    ? {
                          value:
                              normalizeWhatsappPreRegistrationAvailabilityValue(
                                  condition.value
                              )
                      }
                    : {})
            }))
        };
    })
});

export const reorderWhatsappApplicationFlowNodes = (
    flow: WhatsappApplicationFlow,
    sourceIndex: number,
    destinationIndex: number
): WhatsappApplicationFlow => {
    if (
        sourceIndex === destinationIndex ||
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= flow.nodes.length ||
        destinationIndex >= flow.nodes.length
    ) {
        return flow;
    }

    const nodes = [...flow.nodes];
    const [movedNode] = nodes.splice(sourceIndex, 1);
    nodes.splice(destinationIndex, 0, movedNode);

    return {
        ...flow,
        nodes
    };
};

export const applyWhatsappApplicationModelFlow = ({
    currentFlow,
    copiedFlow
}: {
    currentFlow: WhatsappApplicationFlow;
    copiedFlow: WhatsappApplicationFlow;
}): WhatsappApplicationFlow => ({
    ...copiedFlow,
    qualifiedStageId: currentFlow.qualifiedStageId,
    notQualifiedStageId: currentFlow.notQualifiedStageId,
    notQualifiedDisqualifyReason: currentFlow.notQualifiedDisqualifyReason,
    requireApplicationFormRequiredFields:
        currentFlow.requireApplicationFormRequiredFields,
    collectScreeningQuestionnaire: currentFlow.collectScreeningQuestionnaire
});

export const getNextWhatsappApplicationNodeId = (
    nodes: WhatsappApplicationNode[],
    type: WhatsappApplicationNodeType
) => {
    const prefix = `${type}_`;
    const highestIndex = nodes.reduce((highest, node) => {
        if (!node.id.startsWith(prefix)) {
            return highest;
        }
        const index = Number(node.id.slice(prefix.length));
        return Number.isFinite(index) ? Math.max(highest, index) : highest;
    }, 0);

    return `${prefix}${highestIndex + 1}`;
};

const getWhatsappApplicationNodeTargetIds = (node: WhatsappApplicationNode) => [
    ...(node.nextNodeId ? [node.nextNodeId] : []),
    ...node.conditions.map((condition) => condition.nextNodeId)
];

const getReachableWhatsappApplicationNodeIds = (
    nodesById: Map<string, WhatsappApplicationNode>,
    entryNodeId: string
) => {
    const reachable = new Set<string>();
    const pending = [entryNodeId];

    while (pending.length) {
        const nodeId = pending.shift() || "";
        if (reachable.has(nodeId)) {
            continue;
        }
        reachable.add(nodeId);

        const node = nodesById.get(nodeId);
        if (!node) {
            continue;
        }

        for (const targetId of getWhatsappApplicationNodeTargetIds(node)) {
            if (!reachable.has(targetId)) {
                pending.push(targetId);
            }
        }
    }

    return reachable;
};

const whatsappApplicationFlowReachesEnd = (
    node: WhatsappApplicationNode,
    nodesById: Map<string, WhatsappApplicationNode>,
    visited: Set<string>
): boolean => {
    if (node.type === "end") {
        return true;
    }

    if (visited.has(node.id)) {
        return false;
    }
    visited.add(node.id);

    const targetIds = getWhatsappApplicationNodeTargetIds(node);
    if (!targetIds.length) {
        return false;
    }

    return targetIds.every((targetId) => {
        const targetNode = nodesById.get(targetId);
        return targetNode
            ? whatsappApplicationFlowReachesEnd(
                  targetNode,
                  nodesById,
                  new Set(visited)
              )
            : false;
    });
};

const getWhatsappApplicationMissingDefaultNode = (
    nodes: WhatsappApplicationNode[]
) =>
    nodes.find(
        (node) =>
            node.type !== "end" &&
            node.conditions.length > 0 &&
            !node.nextNodeId
    );

export const canPublishWhatsappApplicationFlow = (
    flow: WhatsappApplicationFlow
) => {
    if (!flow.name.trim()) {
        return { canPublish: false, reason: "missingName" };
    }

    if (!flow.nodes.some((node) => node.type === "consent")) {
        return { canPublish: false, reason: "missingConsent" };
    }

    if (!flow.nodes.some((node) => node.type === "end")) {
        return { canPublish: false, reason: "missingEnd" };
    }

    const nodeIds = new Set<string>();
    for (const node of flow.nodes) {
        if (!node.id.trim() || nodeIds.has(node.id)) {
            return { canPublish: false, reason: "invalidNodeId" };
        }
        nodeIds.add(node.id);

        if (node.type !== "end" && !node.text.trim()) {
            return { canPublish: false, reason: "missingNodeText" };
        }
        if (
            node.type === "question" &&
            node.options.filter((option) => option.trim()).length >
                WHATSAPP_APPLICATION_MAX_INTERACTIVE_OPTIONS
        ) {
            return { canPublish: false, reason: "tooManyQuestionOptions" };
        }
    }

    if (getWhatsappApplicationMissingDefaultNode(flow.nodes)) {
        return { canPublish: false, reason: "missingDefaultPath" };
    }

    for (const node of flow.nodes) {
        const targetIds = [
            ...(node.nextNodeId ? [node.nextNodeId] : []),
            ...node.conditions.map((condition) => condition.nextNodeId)
        ];

        for (const targetId of targetIds) {
            if (!nodeIds.has(targetId)) {
                return { canPublish: false, reason: "invalidTarget" };
            }
        }
    }

    const entryNode = flow.nodes[0];
    const nodesById = new Map(flow.nodes.map((node) => [node.id, node]));
    const reachable = getReachableWhatsappApplicationNodeIds(
        nodesById,
        entryNode.id
    );
    if (reachable.size !== flow.nodes.length) {
        return { canPublish: false, reason: "unreachableNode" };
    }

    if (!whatsappApplicationFlowReachesEnd(entryNode, nodesById, new Set())) {
        return { canPublish: false, reason: "missingEndPath" };
    }

    const consentNodeIds = new Set(
        flow.nodes
            .filter((node) => node.type === "consent")
            .map((node) => node.id)
    );
    const visited = new Set<string>();
    const pending = [
        {
            nodeId: entryNode.id,
            hasConsent: false
        }
    ];

    while (pending.length) {
        const current = pending.shift();
        if (!current) {
            continue;
        }
        const visitedKey = `${current.nodeId}:${current.hasConsent}`;
        if (visited.has(visitedKey)) {
            continue;
        }
        visited.add(visitedKey);

        const node = nodesById.get(current.nodeId);
        if (!node) {
            continue;
        }
        const hasConsent = current.hasConsent || consentNodeIds.has(node.id);
        if (
            ["question", "resume", "screeningQuestionnaire"].includes(
                node.type
            ) &&
            !hasConsent
        ) {
            return { canPublish: false, reason: "questionBeforeConsent" };
        }
        for (const targetId of getWhatsappApplicationNodeTargetIds(node)) {
            pending.push({
                nodeId: targetId,
                hasConsent
            });
        }
    }

    return { canPublish: true };
};

export const toggleWhatsappApplicationLinkActive = ({
    active,
    flowId,
    publishedVersion
}: {
    active: boolean;
    flowId?: string;
    publishedVersion: number;
}) => {
    if (!active) {
        return { canActivate: true, flowVersion: publishedVersion || 0 };
    }

    if (!flowId) {
        return { canActivate: false, reason: "missingFlow" };
    }

    if (!publishedVersion) {
        return { canActivate: false, reason: "missingPublishedVersion" };
    }

    return { canActivate: true, flowVersion: publishedVersion };
};

export const getSelectableWhatsappApplicationModels = (
    models: WhatsappApplicationFlow[]
) => models.filter((model) => Number(model.publishedVersion || 0) > 0);

export const getWhatsappApplicationModelIdForSave = ({
    configurationScope,
    routeModelId,
    flowModelId
}: {
    configurationScope: "job" | "model";
    routeModelId?: string;
    flowModelId?: string;
}) => {
    if (configurationScope !== "model") {
        return undefined;
    }

    return routeModelId || flowModelId;
};

export const publishWhatsappApplicationFlowDraft = async ({
    saveDraft,
    publishDraft,
    acceptPublishedFlow,
    syncJobLink
}: {
    saveDraft: () => Promise<WhatsappApplicationFlow | null>;
    publishDraft: (draft: WhatsappApplicationFlow) => Promise<number | null>;
    acceptPublishedFlow?: (flow: WhatsappApplicationFlow) => void;
    syncJobLink?: (flow: WhatsappApplicationFlow) => Promise<void>;
}): Promise<WhatsappApplicationFlow | null> => {
    const savedDraft = await saveDraft();
    if (!savedDraft) {
        return null;
    }

    const publishedVersion = await publishDraft(savedDraft);
    if (!publishedVersion) {
        return null;
    }

    const publishedFlow = {
        ...savedDraft,
        publishedVersion
    };
    acceptPublishedFlow?.(publishedFlow);
    await syncJobLink?.(publishedFlow);

    return publishedFlow;
};

export const isWhatsappApplicationFlowOperationPending = ({
    saving,
    publishing,
    updatingLink
}: {
    saving: boolean;
    publishing: boolean;
    updatingLink: boolean;
}): boolean => saving || publishing || updatingLink;
